import { getPasswordIssues, type PasswordIssue } from '@caterinx/shared-types';
import { Button } from '@caterinx/ui/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@caterinx/ui/components/ui/card';
import { Checkbox } from '@caterinx/ui/components/ui/checkbox';
import { Input } from '@caterinx/ui/components/ui/input';
import { Label } from '@caterinx/ui/components/ui/label';
import { PasswordInput } from '@caterinx/ui/components/ui/password-input';
import { Wordmark } from '@caterinx/ui/components/ui/wordmark';
import { useToast } from '@caterinx/ui/hooks/use-toast';
import { isAxiosError } from 'axios';
import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '../../lib/api.js';
import { LEGAL_LINKS } from '../../lib/legal.js';
import { recaptcha } from '../../lib/recaptcha.js';
import { authQuery, safeRedirect } from '../../lib/redirect.js';

/**
 * Übersetzt die Regelverstöße aus `@caterinx/shared-types`.
 *
 * **Dieselbe Funktion prüft im Backend** (`register.ts`) und definiert die Cognito-Policy
 * (`auth-stack.ts`). Drei Stellen, eine Quelle — genau deshalb kann hier nicht passieren, was
 * in Atleo passiert ist: eine Cognito-Regel, die Passwörter ablehnt, ohne dass die
 * Oberfläche sie je angezeigt hätte.
 */
const ISSUE_TEXT: Record<PasswordIssue, string> = {
  too_short: 'Mindestens 8 Zeichen',
  too_long: 'Höchstens 256 Zeichen',
  whitespace_edges: 'Keine Leerzeichen am Anfang oder Ende',
  too_common: 'Kein offensichtlich unsicheres Passwort',
};

export function RegisterPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Vorbelegt, wenn die Einladungsseite (D32) Adresse und Betriebsname mitgibt; editierbar.
  const [companyName, setCompanyName] = useState(searchParams.get('company') ?? '');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Nur In-App-Pfade (Open-Redirect-Schutz). Zeigt er auf eine Einladung, steht der Auftrag
  // im Mittelpunkt und der Text unten ändert sich.
  const redirect = safeRedirect(searchParams.get('redirect'));
  const isInvited = redirect?.startsWith('/auftrag/') === true;

  /**
   * Ohne Site-Key (lokale Entwicklung) läuft keine Bot-Abwehr — dann entfällt auch der
   * Hinweis darauf.
   *
   * **Die Einwilligung ist mit D27 entfallen.** Bis zum 21.09.2026 stand hier erst ein
   * Pflicht-Häkchen und später ein beobachteter Banner-Zustand; beides ist weg, seit der
   * Betreiber reCAPTCHA als betriebsnotwendigen Dienst führt. Für diese Seite ist das die
   * dritte Fassung derselben Stelle — und die einzige ohne Zwischenschritt vor dem Knopf, mit
   * dem ein Gastronom zum ersten Mal Vertrauen investiert.
   */
  const botProtectionRequired = recaptcha.siteKey !== '';

  const passwordIssues = useMemo(() => (password ? getPasswordIssues(password) : []), [password]);
  /**
   * Was den Knopf freigibt, steht vollständig **im Formular**: Pflichtfelder, ein gültiges
   * Passwort, die Zustimmung zu den Nutzungsbedingungen. Keine Bedingung mehr, die anderswo auf
   * der Seite erfüllt werden müsste — die Einwilligung in den Bot-Schutz war die letzte, und
   * die ist mit D27 entfallen.
   */
  const canSubmit = companyName && email && password && passwordIssues.length === 0 && termsAccepted;

  const handleSubmit = async (formEvent: FormEvent): Promise<void> => {
    formEvent.preventDefault();

    setIsSubmitting(true);

    try {
      const recaptchaToken = await recaptcha.token('REGISTER');

      /**
       * **Kein Token trotz erteilter Einwilligung.** Dann ist das Google-Skript nicht
       * angelaufen: ein Werbe- oder Skriptblocker, eine Netzsperre — oder die Domain fehlt in
       * der Schlüsselkonfiguration bei Google.
       *
       * Vorher ging die Anfrage trotzdem heraus, und der Server antwortete mit
       * „reCAPTCHA-Token fehlt." Das ist wahr und hilft niemandem: Ein Gastronom, der zum
       * ersten Mal ein Konto anlegt, liest dort einen technischen Begriff und weiß nicht, dass
       * die Ursache in seinem Browser liegen könnte. Die Registrierung scheiterte am Server
       * ohnehin — hier scheitert sie eine Sekunde früher und mit einer Erklärung.
       */
      if (botProtectionRequired && recaptchaToken === null) {
        toast({
          variant: 'destructive',
          title: 'Bot-Schutz konnte nicht geladen werden',
          description:
            'Das Sicherheitsmodul von Google ist nicht angelaufen. Meist liegt es an einem ' +
            'Werbe- oder Skriptblocker — bitte für diese Seite ausnehmen und erneut versuchen. ' +
            'Bleibt es dabei, schreiben Sie uns an angebote@caterinx.com.',
        });
        return;
      }

      await api.post('/auth/register', {
        email,
        password,
        companyName,
        contactName,
        recaptchaToken,
        siteKey: recaptcha.siteKey || undefined,
      });
      toast({
        title: 'Konto angelegt',
        description: 'Wir haben Ihnen einen Bestätigungscode geschickt.',
      });
      navigate(`/verify-email?${authQuery({ email, redirect })}`);
    } catch (error) {
      const data = isAxiosError(error) ? (error.response?.data as { code?: string; message?: string }) : undefined;

      toast({
        variant: 'destructive',
        title: 'Registrierung fehlgeschlagen',
        description:
          data?.code === 'EMAIL_EXISTS'
            ? 'Zu dieser E-Mail-Adresse gibt es bereits ein Konto.'
            : (data?.message ?? 'Bitte versuchen Sie es in einem Moment erneut.'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      /*
       * Der zusätzliche Fußraum für das Einwilligungsbanner ist mit D27 entfallen: Im Portal
       * läuft kein einwilligungspflichtiger Dienst mehr, also steht dort auch kein Banner, das
       * den Knopf verdecken könnte.
       */
      className="flex min-h-screen flex-col items-center justify-center gap-8 p-4"
    >
      <Wordmark className="w-44" />
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Als Catering-Anbieter registrieren</CardTitle>
          <CardDescription>
            {isInvited
              ? 'Danach geben Sie direkt Ihr Angebot zu dem Auftrag ab, zu dem wir Sie eingeladen haben. Für weitere Ausschreibungen prüfen wir anschließend Ihre Angaben.'
              : 'Nach der Registrierung prüfen wir Ihre Angaben. Erst danach erhalten Sie Ausschreibungen.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="companyName">Firmenname</Label>
              <Input
                id="companyName"
                required
                value={companyName}
                onChange={(changeEvent) => setCompanyName(changeEvent.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contactName">Ansprechpartner</Label>
              <Input
                id="contactName"
                value={contactName}
                onChange={(changeEvent) => setContactName(changeEvent.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">E-Mail</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(changeEvent) => setEmail(changeEvent.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Passwort</Label>
              <PasswordInput
                id="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(changeEvent) => setPassword(changeEvent.target.value)}
              />
              {password && passwordIssues.length > 0 && (
                <ul className="text-destructive space-y-1 text-sm">
                  {passwordIssues.map((issue) => (
                    // eslint-disable-next-line security/detect-object-injection -- `issue` ist ein PasswordIssue-Literal aus shared-types, keine Nutzereingabe
                    <li key={issue}>{ISSUE_TEXT[issue]}</li>
                  ))}
                </ul>
              )}
            </div>

            {/*
              **Nur noch ein Häkchen.** Die Annahme der Nutzungsbedingungen ist die Grundlage
              des Vertrags und gehört deshalb ins Formular. Die Einwilligung in reCAPTCHA ist
              eine datenschutzrechtliche Einwilligung nach Art. 6 Abs. 1 lit. a DSGVO — sie
              braucht ihre eigene, ungekoppelte Entscheidung, aber nicht diesen Platz. Sie
              liegt im Banner am Seitenrand, so wie bei jedem anderen Anbieter, der reCAPTCHA
              einsetzt. Zwei Häkchen übereinander waren die Kopplung vermeiden wollende,
              trotzdem abschreckende Variante.
            */}
            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                  className="mt-0.5"
                />
                <Label htmlFor="terms" className="text-muted-foreground text-sm leading-relaxed font-normal">
                  Ich habe die{' '}
                  <a href={LEGAL_LINKS.termsSupplier} target="_blank" rel="noopener noreferrer" className="underline">
                    Nutzungsbedingungen für Catering-Betriebe
                  </a>{' '}
                  gelesen und akzeptiere sie. Die{' '}
                  <a href={LEGAL_LINKS.privacy} target="_blank" rel="noopener noreferrer" className="underline">
                    Datenschutzerklärung
                  </a>{' '}
                  habe ich zur Kenntnis genommen.
                </Label>
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={!canSubmit || isSubmitting}>
              {isSubmitting ? 'Wird angelegt...' : 'Konto anlegen'}
            </Button>

            {/*
              Der Standardhinweis, den Google für den Einsatz von reCAPTCHA verlangt, wenn das
              sichtbare Badge ausgeblendet ist — und zugleich das, was ein Gastronom an dieser
              Stelle wirklich braucht: die Information, dass ein Drittdienst im Spiel ist, mit
              dem Weg zu den Einzelheiten.

              **Kein Widerrufslink mehr.** Seit D27 läuft die Bot-Abwehr auf berechtigtem
              Interesse; es gibt keine Einwilligung, die man zurücknehmen könnte. Der Weg für
              den Widerspruch nach Art. 21 DSGVO steht in der Datenschutzerklärung.
            */}
            {botProtectionRequired && (
              <p className="text-muted-foreground text-xs leading-relaxed">
                Diese Seite ist durch Google reCAPTCHA vor automatisierten Registrierungen geschützt. Es gelten die{' '}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Datenschutzerklärung
                </a>{' '}
                und die{' '}
                <a
                  href="https://policies.google.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Nutzungsbedingungen
                </a>{' '}
                von Google.
              </p>
            )}
          </form>

          <p className="text-muted-foreground mt-6 text-sm">
            Bereits registriert?{' '}
            <Link to={redirect ? `/login?${authQuery({ email, redirect })}` : '/login'} className="underline">
              Anmelden
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
